import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2, MessageCircle, Home, Copy } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { useConfig } from "@/context/ConfigContext";
import { fmt, SITE, PAYMENT_METHODS } from "@/lib/site";

export default function OrderSuccess() {
    const { state } = useLocation();
    const nav = useNavigate();
    const { hasWhatsApp, waHref } = useConfig();
    const order = state?.order;
    const [pay, setPay] = useState(null);

    useEffect(() => {
        if (!order) nav("/");
    }, [order, nav]);

    useEffect(() => {
        api.get("/settings/payments").then((r) => setPay(r.data)).catch(() => {});
    }, []);

    if (!order) return null;

    const metodo = PAYMENT_METHODS.find((m) => m.id === order.metodo_pago);
    const instrucciones = pay?.[order.metodo_pago]?.instrucciones || "";
    const copyAmount = () => { navigator.clipboard?.writeText(order.total.toFixed(2)); toast.success("Monto copiado"); };
    const lines = order.items.map((i) => `• ${i.cantidad}x ${i.nombre} — ${fmt(i.precio_aplicado)} c/u`).join("\n");
    const wa = waHref(
        `¡Hola ${SITE.name}! 🌲\nNuevo pedido *${order.order_number}*\n\nCliente: ${order.cliente.nombre}\nTel: ${order.cliente.telefono}\nTipo: ${order.cliente.tipo}\nPago: ${metodo?.label || order.metodo_pago}\n\n${lines}\n\n*TOTAL: ${fmt(order.total)}*\n${order.cliente.direccion ? "Envío: " + order.cliente.direccion : ""}`
    );

    return (
        <div className="bg-crema min-h-screen">
            <Navbar />
            <div className="pt-28 md:pt-36 pb-24 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div initial={{ scale: 0, rotate: -20 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 200, damping: 15 }}>
                    <CheckCircle2 size={72} className="text-verde" strokeWidth={1.5} />
                </motion.div>
                <h1 className="font-display font-extrabold text-4xl md:text-6xl text-ink mt-6 tracking-tight">¡Pedido recibido!</h1>
                <p className="text-mut mt-4">
                    Tu número de pedido es <span className="text-naranja font-bold" data-testid="order-number">{order.order_number}</span>. {hasWhatsApp ? "Para confirmarlo y coordinar el pago, envíanoslo por WhatsApp." : "Nos pondremos en contacto contigo para confirmarlo y coordinar el pago."}
                </p>

                {pay && (
                <div className="mt-8 rounded-2xl border border-madera/40 bg-madera/5 p-6" data-testid="payment-instructions">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div>
                            <span className="text-xs uppercase tracking-widest text-madera-dark font-semibold">Cómo pagar</span>
                            <h2 className="font-display font-bold text-xl text-ink mt-0.5">{metodo?.label}</h2>
                        </div>
                        <button onClick={copyAmount} data-testid="copy-amount" className="flex items-center gap-2 bg-white border border-linea rounded-full px-4 py-2 text-sm font-semibold text-verde hover:border-madera transition-colors">
                            {fmt(order.total)} <Copy size={14} />
                        </button>
                    </div>
                    {instrucciones ? (
                        <pre className="mt-4 whitespace-pre-wrap font-sans text-sm text-ink/80 leading-relaxed">{instrucciones}</pre>
                    ) : (
                        <p className="mt-4 text-sm text-mut">Te enviaremos los datos de pago a la brevedad.</p>
                    )}
                    <p className="mt-4 text-xs text-mut">Realiza el pago por el monto exacto y guarda tu <strong>comprobante</strong> junto con tu número de pedido.</p>
                </div>
                )}

                {hasWhatsApp && (
                    <a href={wa} target="_blank" rel="noreferrer" data-testid="whatsapp-order-btn" className="mt-6 inline-flex items-center gap-3 bg-[#25D366] text-white rounded-full px-8 py-4 font-semibold hover:brightness-95 transition-all">
                        <MessageCircle size={20} /> Ya pagué — enviar comprobante por WhatsApp
                    </a>
                )}

                <div className="mt-12 border border-linea rounded-2xl bg-hueso overflow-hidden shadow-soft">
                    <div className="p-6 border-b border-linea flex justify-between items-center">
                        <span className="font-display font-bold text-xl">Detalle del pedido</span>
                        <span className="text-xs text-mut uppercase">{metodo?.label}</span>
                    </div>
                    <div className="divide-y divide-linea">
                        {order.items.map((i, idx) => (
                            <div key={idx} className="p-4 flex justify-between gap-4">
                                <div>
                                    <p className="text-sm text-ink">{i.nombre}</p>
                                    <p className="text-xs text-mut">{i.cantidad} × {fmt(i.precio_aplicado)} {i.es_mayoreo && <span className="text-madera-dark font-medium">· mayoreo</span>}</p>
                                </div>
                                <span className="text-sm font-medium">{fmt(i.total_linea)}</span>
                            </div>
                        ))}
                    </div>
                    <div className="p-6 flex justify-between items-end hero-gradient text-white">
                        <span className="text-xs uppercase tracking-widest text-white/60">Total</span>
                        <span className="font-display font-extrabold text-3xl">{fmt(order.total)} <span className="text-sm font-sans font-semibold opacity-70">+ IVA</span></span>
                    </div>
                </div>

                <div className="flex flex-wrap gap-3 mt-10">
                    <Link to={`/pedido/${order.order_number}`} data-testid="ir-seguimiento" className="rounded-full bg-verde text-white px-6 py-3.5 font-medium hover:bg-verde-800 transition-colors">Seguir mi pedido y subir el pago</Link>
                    <Link to="/tienda" className="rounded-full border border-verde text-verde px-6 py-3.5 font-medium hover:bg-verde hover:text-white transition-colors">Seguir comprando</Link>
                    <Link to="/" className="flex items-center gap-2 px-6 py-3.5 font-medium text-mut hover:text-verde"><Home size={16} /> Inicio</Link>
                </div>
            </div>
        </div>
    );
}
